/**
 * ResearchPageScaffold — the "Page Contract" wrapper.
 *
 * Every public research detail page (vaccine, ingredient, disease,
 * manufacturer, surveillance system, schedule, age cohort, comparison,
 * and any FUTURE explorer's pages) is composed inside this scaffold so
 * the 4 user questions are answered consistently across the site:
 *
 *   1. What is it?            → `heroSlot`
 *   2. What is it connected to? → `connectedSlot`
 *   3. What evidence exists?    → `evidenceSlot`
 *   4. Where should I explore next? → `continueSlot`
 *
 * Also handles SEO scaffolding: canonical link, JSON-LD type injection,
 * page-level SaveButton in the top-right (so the whole page is savable).
 *
 * Reusable across all explorers — no vaccine-specific assumptions live
 * here. Each explorer composes its own hero / connected / evidence /
 * continue contents, then drops them in.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SEO from "../SEO";
import SaveButton from "./SaveButton";

/**
 * Props
 *   title          Page title (also used in <head>)
 *   description    Meta description
 *   canonicalPath  Canonical path (without origin)
 *   breadcrumbs    [{ label, href? }]
 *   jsonLd         Optional JSON-LD object
 *   bookmark       { kind, refSlug, refTitle, parentRef? } for top-right Save
 *   eyebrow        Optional small label above title
 *   heroSlot       Q1: What is it?
 *   connectedSlot  Q2: What is it connected to?
 *   evidenceSlot   Q3: What evidence exists? (vaccine pages use this for Safety & Evidence)
 *   continueSlot   Q4: Where should I explore next? (ContinueExploring component)
 */
export default function ResearchPageScaffold({
    title,
    description,
    canonicalPath,
    breadcrumbs = [],
    jsonLd = null,
    bookmark = null,
    eyebrow = null,
    reviewStamp = null,
    heroSlot,
    connectedSlot = null,
    evidenceSlot = null,
    continueSlot = null,
}) {
    // Smooth-scroll to fragments after mount (so saved section anchors work
    // when the user clicks back from My Research).
    useEffect(() => {
        const hash = window.location.hash;
        if (!hash) return;
        const id = hash.slice(1);
        setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
    }, []);

    return (
        <div className="min-h-screen bg-[#0B1220] text-[#CBD5E1]" data-testid="research-page-scaffold">
            <SEO
                title={title}
                description={description}
                canonicalPath={canonicalPath}
                jsonLd={jsonLd}
            />

            {/* Top accent line */}
            <div className="h-px bg-gradient-to-r from-transparent via-[#56CCF2]/70 to-transparent" />

            {/* Breadcrumbs */}
            {breadcrumbs.length > 0 ? (
                <nav
                    aria-label="Breadcrumb"
                    className="max-w-6xl mx-auto px-6 pt-6 text-[11px] text-[#64748B] flex items-center gap-1.5"
                    data-testid="research-page-breadcrumbs"
                >
                    {breadcrumbs.map((b, i) => (
                        <React.Fragment key={i}>
                            {b.href ? (
                                <Link to={b.href} className="hover:text-[#56CCF2] transition-colors">{b.label}</Link>
                            ) : (
                                <span className="text-[#94A3B8]">{b.label}</span>
                            )}
                            {i < breadcrumbs.length - 1 ? <ChevronRight className="w-3 h-3 text-[#475569]" /> : null}
                        </React.Fragment>
                    ))}
                </nav>
            ) : null}

            {/* HERO — Q1: What is it? */}
            <header className="max-w-6xl mx-auto px-6 pt-8 pb-12">
                <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0 flex-1">
                        {eyebrow ? (
                            <div className="text-[10px] uppercase tracking-[0.28em] text-[#56CCF2] font-semibold mb-2.5">{eyebrow}</div>
                        ) : null}
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.05] mb-4">
                            {title}
                        </h1>
                        {reviewStamp ? <div className="mb-4">{reviewStamp}</div> : null}
                        {description ? (
                            <p className="text-[15px] text-[#94A3B8] leading-relaxed max-w-3xl">{description}</p>
                        ) : null}
                    </div>
                    {bookmark ? (
                        <div className="shrink-0 pt-2">
                            <SaveButton
                                kind={bookmark.kind}
                                refSlug={bookmark.refSlug}
                                refTitle={bookmark.refTitle}
                                parentRef={bookmark.parentRef}
                                refUrl={canonicalPath}
                                variant="pill"
                            />
                        </div>
                    ) : null}
                </div>
                {heroSlot ? <div className="mt-8">{heroSlot}</div> : null}
            </header>

            {/* Q2 + Q3 — Connected + Evidence */}
            {(connectedSlot || evidenceSlot) ? (
                <div className="max-w-6xl mx-auto px-6 space-y-12 pb-12">
                    {connectedSlot ? <div data-testid="research-page-connected">{connectedSlot}</div> : null}
                    {evidenceSlot ? <div data-testid="research-page-evidence">{evidenceSlot}</div> : null}
                </div>
            ) : null}

            {/* Q4 — Where to next */}
            {continueSlot}

            <div className="h-px bg-gradient-to-r from-transparent via-[#243044] to-transparent" />
            <footer className="max-w-6xl mx-auto px-6 py-10 text-[11px] uppercase tracking-[0.22em] text-[#64748B]">
                <Link to="/" className="hover:text-[#56CCF2] transition-colors">← Back to the library</Link>
            </footer>
        </div>
    );
}
